import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Result, Button, Card, Typography, Descriptions, Tag, Space, Spin, Alert } from 'antd';
import { CheckCircleOutlined, HomeOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import { useAutoScroll } from '../../hooks/useAutoScroll';

const { Title, Text } = Typography;

function BookingConfirmation() {
	const location = useLocation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const navState = location.state || {};
	const [booking, setBooking] = useState(navState.booking || null);
	const [loading, setLoading] = useState(!navState.booking);
	const [error, setError] = useState('');
	const orderData = navState.orderData;
	const payment = navState.payment;

	useEffect(() => {
		const load = async () => {
			if (booking) return; // already provided via state
			try {
				setLoading(true);
				const idFromQuery = searchParams.get('id') || searchParams.get('booking_id');
				const lastId = localStorage.getItem('last_booking_id');
				const id = idFromQuery || lastId;
				if (!id) {
					setError('Missing booking reference.');
					return;
				}
				const res = await api.getBooking(id);
				setBooking(res.data);
			} catch (e) {
				setError('Failed to load booking details.');
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [booking, searchParams]);

	// Auto-scroll to top when component mounts
	useAutoScroll(true, 'smooth');

	const getStatusColor = (status) => {
		switch (status) {
			case 'confirmed':
				return 'green';
			case 'pending':
				return 'orange';
			case 'cancelled':
				return 'red';
			default:
				return 'blue';
		}
	};

	if (loading) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<Spin size="large" />
			</div>
		);
	}

	if (!booking) {
		return (
			<Result
				status="404"
				title="Booking Not Found"
				subTitle={error || 'Sorry, the booking information could not be found.'}
				extra={
					<Button type="primary" onClick={() => navigate('/')}>Back Home</Button>
				}
			/>
		);
	}

	return (
		<div className="min-h-screen bg-gray-50 py-8">
			<div className="max-w-4xl mx-auto px-4">
				<Result
					icon={<CheckCircleOutlined className="text-green-500" />}
					title="Booking Confirmed!"
					subTitle={`Your booking has been successfully placed. Booking ID: ${booking.booking_number || booking.id}`}
					extra={[
						<Button type="primary" key="home" onClick={() => navigate('/')}> 
							<HomeOutlined /> Back to Home
						</Button>,
						<Button key="menu" onClick={() => navigate('/menu')}>
							<ShoppingCartOutlined /> Browse Menu
						</Button>
					]}
				/>

				{payment && (
					<Alert
						message="Payment Successful"
						description={
							<div className="mt-2">
								<div>Transaction ID: <Text code>{payment.transaction_id}</Text></div>
								<div>Amount: <Text strong>₹{payment.amount?.toLocaleString()}</Text></div>
								<div>Method: {payment.payment_method}</div>
							</div>
						}
						type="success"
						showIcon
						className="mb-6"
					/>
				)}

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
					<Card title="Booking Details" className="shadow-sm">
						<Descriptions column={1} size="small">
							<Descriptions.Item label="Booking ID">
								<Text strong>{booking.booking_number || booking.id}</Text>
							</Descriptions.Item>
							<Descriptions.Item label="Status">
								<Tag color={getStatusColor(booking.status)}>
									{booking.status?.toUpperCase() || 'CONFIRMED'}
								</Tag>
							</Descriptions.Item>
							<Descriptions.Item label="Event Date">
								{booking.event_date ? dayjs(booking.event_date).format('MMMM DD, YYYY') : '-'}
							</Descriptions.Item>
							<Descriptions.Item label="Event Time">
								{booking.event_time ? dayjs(booking.event_time, 'HH:mm').format('hh:mm A') : '-'}
							</Descriptions.Item>
							<Descriptions.Item label="Guest Count">
								{booking.guest_count || '-'} guests
							</Descriptions.Item>
							<Descriptions.Item label="Total Amount">
								<Text strong className="text-orange-600">
									₹{(booking.total_amount || 0).toLocaleString()}
								</Text>
							</Descriptions.Item>
							<Descriptions.Item label="Payment Method">
								<Tag color={booking.payment_method === 'online' ? 'blue' : 'orange'}>
									{booking.payment_method === 'online' ? 'Online Payment' : 'Cash on Delivery'}
								</Tag>
							</Descriptions.Item>
						</Descriptions>
					</Card>

					<Card title="Contact Information" className="shadow-sm">
						<Descriptions column={1} size="small">
							<Descriptions.Item label="Name">
								{booking.customer_name || orderData?.name}
							</Descriptions.Item>
							<Descriptions.Item label="Email">
								{booking.customer_email || orderData?.email}
							</Descriptions.Item>
							<Descriptions.Item label="Phone">
								{booking.customer_phone || orderData?.phone}
							</Descriptions.Item>
							<Descriptions.Item label="Event Address">
								<Text>{booking.address || orderData?.address}</Text>
							</Descriptions.Item>
							{(booking.special_requests || orderData?.special_requests) && (
								<Descriptions.Item label="Special Requests">
									<Text type="secondary">
										{booking.special_requests || orderData?.special_requests}
									</Text>
								</Descriptions.Item>
							)}
						</Descriptions>
					</Card>
				</div>

				{booking.items && booking.items.length > 0 && (
					<Card title="Order Items" className="shadow-sm mt-6">
						<div className="space-y-4">
							{booking.items.map((item, index) => (
								<div key={index} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
									<div className="flex-1">
										<Title level={5} className="mb-1">
											{item.menu_item?.name || item.name}
										</Title>
										<Text type="secondary">
											Quantity: {item.quantity} × ₹{item.price}
										</Text>
										{item.customizations && Object.keys(item.customizations).length > 0 && (
											<div className="mt-2">
												{Object.entries(item.customizations).map(([key, value]) => (
													<Tag key={key} size="small" className="mr-1">
														{key}: {value}
													</Tag>
												))}
											</div>
										)}
									</div>
									<Text strong className="text-orange-600">
										₹{(item.price * item.quantity).toLocaleString()}
									</Text>
								</div>
							))}
						</div>
					</Card>
				)}

				<Card className="shadow-sm mt-6 bg-blue-50 border-blue-200">
					<div className="text-center">
						<Title level={4} className="text-blue-800 mb-2">
							What's Next?
						</Title>
						<Space direction="vertical" size="small">
							<Text>• Our team will contact you within 24 hours to confirm details</Text>
							<Text>• You'll receive updates via email and SMS</Text>
							<Text>• For any queries, contact us at support@cateringplatform.com</Text>
						</Space>
					</div>
				</Card>
			</div>
		</div>
	);
}

export default BookingConfirmation;
